import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Divider
} from '@mui/material';
import {
  Event,
  LocationOn,
  Phone,
  Email,
  AccessTime,
  Church
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Dados de exemplo para demonstração
const demoFestejo = {
  title: 'Festa de São João Batista 2024',
  padroeiro: 'São João Batista',
  description: 'Celebração tradicional em honra a São João Batista, padroeiro dos nascimentos e protetor das crianças. Uma festa que une fé, tradição e alegria em nossa comunidade.',
  paroquia: 'Paróquia São João Batista',
  endereco: 'Rua das Flores, 123',
  cidade: 'Campinas',
  estado: 'SP',
  contactEmail: 'festejo@saojoao.com.br',
  contactPhone: '(19) 99999-8888',
  startDate: '2024-06-20',
  endDate: '2024-06-24',
  theme: {
    primaryColor: '#2e7d32',
    secondaryColor: '#ff9800',
    fontFamily: 'Roboto'
  },
  coverImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800',
  sections: [
    {
      id: '1',
      type: 'texto' as const,
      title: 'História de São João Batista',
      content: 'São João Batista foi o precursor de Jesus Cristo, nascido seis meses antes do Salvador. Sua festa é celebrada no dia 24 de junho, marcando o início das tradicionais festas juninas.\n\nAs celebrações em honra a São João incluem fogueiras, que simbolizam a luz de Cristo, e diversas tradições populares que unem fé e cultura.',
      order: 0
    },
    {
      id: '2',
      type: 'programacao' as const,
      title: 'Programação da Festa',
      events: [
        {
          id: '1',
          title: 'Missa de Abertura',
          dateTime: '2024-06-20T19:00:00',
          location: 'Igreja Matriz',
          description: 'Missa solene de abertura da festa com bênção dos fiéis'
        },
        {
          id: '2',
          title: 'Quermesse Tradicional',
          dateTime: '2024-06-21T18:00:00',
          location: 'Pátio da Igreja',
          description: 'Comidas típicas, brincadeiras e apresentações culturais'
        },
        {
          id: '3',
          title: 'Procissão de São João',
          dateTime: '2024-06-24T16:00:00',
          location: 'Saída da Igreja',
          description: 'Procissão tradicional pelas ruas do bairro'
        },
        {
          id: '4',
          title: 'Missa Solene e Fogueira',
          dateTime: '2024-06-24T19:00:00',
          location: 'Igreja e Pátio',
          description: 'Missa principal seguida da tradicional fogueira de São João'
        }
      ],
      order: 1
    },
    {
      id: '3',
      type: 'imagem' as const,
      title: 'Nossa Igreja',
      imageUrl: 'https://images.unsplash.com/photo-1520637836862-4d197d17c90a?w=600',
      description: 'Igreja Matriz São João Batista, local das principais celebrações',
      order: 2
    }
  ]
};

const FestojoDemo: React.FC = () => {
  // Formatar data para exibição
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), "dd 'de' MMMM", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  // Formatar data e hora para eventos
  const formatEventDateTime = (dateTime: string): string => {
    try {
      const date = new Date(dateTime);
      return format(date, "dd/MM 'às' HH:mm", { locale: ptBR });
    } catch {
      return 'Data inválida';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      fontFamily: demoFestejo.theme.fontFamily
    }}>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: demoFestejo.theme.primaryColor,
          color: 'white',
          py: 6,
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${demoFestejo.coverImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              textAlign: 'center',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            {demoFestejo.title}
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              textAlign: 'center',
              opacity: 0.9,
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            Em honra a {demoFestejo.padroeiro}
          </Typography>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Chip
              icon={<Event />}
              label={`${formatDate(demoFestejo.startDate)} a ${formatDate(demoFestejo.endDate)}`}
              sx={{
                backgroundColor: demoFestejo.theme.secondaryColor,
                color: 'white',
                fontSize: '1.1rem',
                py: 2,
                px: 1
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Conteúdo principal */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Descrição */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color={demoFestejo.theme.primaryColor}>
            Bem-vindos à nossa festa!
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.2rem', maxWidth: 800, mx: 'auto' }}>
            {demoFestejo.description}
          </Typography>
        </Box>

        {/* Seções dinâmicas */}
        {demoFestejo.sections.map((section) => (
          <Box key={section.id} sx={{ mb: 6 }}>
            {section.type === 'texto' && (
              <Paper sx={{ p: 4 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  color={demoFestejo.theme.primaryColor}
                  sx={{ borderBottom: `3px solid ${demoFestejo.theme.secondaryColor}`, pb: 1, mb: 3 }}
                >
                  {section.title}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontSize: '1.1rem', 
                    lineHeight: 1.8,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {section.content}
                </Typography>
              </Paper>
            )}

            {section.type === 'programacao' && (
              <Paper sx={{ p: 4 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  color={demoFestejo.theme.primaryColor}
                  sx={{ borderBottom: `3px solid ${demoFestejo.theme.secondaryColor}`, pb: 1, mb: 3 }}
                >
                  {section.title}
                </Typography>
                <Grid container spacing={3}>
                  {section.events.map((event) => (
                    <Grid item xs={12} md={6} key={event.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          border: `2px solid ${demoFestejo.theme.primaryColor}`,
                          '&:hover': {
                            boxShadow: 4
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AccessTime color="primary" />
                            <Chip
                              label={formatEventDateTime(event.dateTime)}
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="h6" gutterBottom color={demoFestejo.theme.primaryColor}>
                            {event.title}
                          </Typography>
                          {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {event.location}
                              </Typography>
                            </Box>
                          )}
                          {event.description && (
                            <Typography variant="body2">
                              {event.description}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {section.type === 'imagem' && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  color={demoFestejo.theme.primaryColor}
                  sx={{ borderBottom: `3px solid ${demoFestejo.theme.secondaryColor}`, pb: 1, mb: 3 }}
                >
                  {section.title}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <img
                    src={section.imageUrl}
                    alt={section.title}
                    style={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 8,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}
                  />
                </Box>
                {section.description && (
                  <Typography variant="body1" color="text.secondary">
                    {section.description}
                  </Typography>
                )}
              </Paper>
            )}
          </Box>
        ))}

        {/* Informações de contato */}
        <Paper sx={{ p: 4, backgroundColor: demoFestejo.theme.primaryColor, color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Informações e Contato
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon sx={{ color: 'white' }}>
                    <Church />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Paróquia"
                    secondary={demoFestejo.paroquia}
                    sx={{ '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.8)' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ color: 'white' }}>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Endereço"
                    secondary={`${demoFestejo.endereco}, ${demoFestejo.cidade} - ${demoFestejo.estado}`}
                    sx={{ '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.8)' } }}
                  />
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon sx={{ color: 'white' }}>
                    <Email />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Email"
                    secondary={demoFestejo.contactEmail}
                    sx={{ '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.8)' } }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ color: 'white' }}>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Telefone"
                    secondary={demoFestejo.contactPhone}
                    sx={{ '& .MuiListItemText-secondary': { color: 'rgba(255,255,255,0.8)' } }}
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: demoFestejo.theme.secondaryColor,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  backgroundColor: demoFestejo.theme.secondaryColor,
                  opacity: 0.9
                }
              }}
            >
              Venha participar!
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box 
        sx={{ 
          backgroundColor: '#333',
          color: 'white',
          py: 3,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2">
            © 2024 {demoFestejo.paroquia}. Site criado com o Sistema de Festejos.
          </Typography>
        </Container>
      </Box>

      {/* Aviso de demonstração */}
      <Box
        sx={{
          position: 'fixed',
          top: 80,
          right: 20,
          backgroundColor: 'rgba(0,0,0,0.8)',
          color: 'white',
          p: 2,
          borderRadius: 1,
          zIndex: 1000,
          maxWidth: 300
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          🎉 Demonstração
        </Typography>
        <Typography variant="caption">
          Este é um exemplo de como o site do festejo ficará após a criação. 
          Todos os dados são fictícios para fins de demonstração.
        </Typography>
      </Box>
    </Box>
  );
};

export default FestojoDemo;

